import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { getHospitalUserList, sendNotification } from '@utils/sharedUtils';
import { useAppSelector } from '@store/hooks';
import { HOSPITAL } from '@constants/constants';

type UserOption = {
    userId: string;
    name: string;
    email: string;
    mobileNumber: string;
};

const CreateNotification: React.FC = () => {
    const [isScheduled, setIsScheduled] = useState(false);
    const [selectedType, setSelectedType] = useState('email');
    const [searchResults, setSearchResults] = useState<
        { label: string; value: string }[]
    >([]);
    const [selectedUsers, setSelectedUsers] = useState<
        MultiValue<{ label: string; value: string }>
    >([]);
    const [userList, setUserList] = useState<UserOption[]>([]);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [publishDate, setPublishDate] = useState('');
    const [publishTime, setPublishTime] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );

    const ToggleOnSVG = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="50"
            height="31"
            viewBox="0 0 65 31"
            fill="none"
        >
            <rect
                x="0.5"
                y="0.5"
                width="60"
                height="31"
                rx="15"
                fill="#D5E6FF"
                stroke="#0346A2"
            />
            <circle cx="48" cy="16" r="12" fill="#0346A2" />
        </svg>
    );

    const ToggleOffSVG = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="50"
            height="31"
            viewBox="0 0 65 31"
            fill="none"
        >
            <rect
                x="0.5"
                y="0.5"
                width="60"
                height="31"
                rx="15"
                fill="white"
                stroke="#0346A2"
            />
            <circle cx="17" cy="16" r="12" fill="#0346A2" />
        </svg>
    );

    const fetchData = async () => {
        try {
            if (loggedInUserRole.toLowerCase() === HOSPITAL.toLowerCase()) {
                const data = await getHospitalUserList(loggedInUserTenantID);
                setUserList(data);
                updateSearchResults(data, 'email');
            }
        } catch (error: any) {
            console.error('Error fetching user list:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const updateSearchResults = (userData: UserOption[], type: string) => {
        const formattedData = userData.map((user) => ({
            value: user.userId,
            label: `${user.name} - ${
                type === 'email' ? user.email : user.mobileNumber
            }`,
        }));

        setSearchResults([
            { label: 'Select All', value: 'select_all' },
            ...formattedData,
        ]);
    };

    const handleToggle = () => {
        setIsScheduled(!isScheduled);
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;
        setSelectedType(selectedValue);
        updateSearchResults(userList, selectedValue);
    };

    const handleSearch = (inputValue: string) => {
        if (inputValue.length > 2) {
            const filteredUsers = userList.filter((user) => {
                const label = `${user.name} - ${
                    selectedType === 'email' ? user.email : user.mobileNumber
                }`;
                return label.toLowerCase().includes(inputValue.toLowerCase());
            });
            updateSearchResults(filteredUsers, selectedType);
        } else {
            updateSearchResults(userList, selectedType);
        }
    };

    const handleSelectUsers = (
        selectedOptions: MultiValue<{ label: string; value: string }>
    ) => {
        if (selectedOptions.some((option) => option.value === 'select_all')) {
            setSelectedUsers(
                searchResults.filter((option) => option.value !== 'select_all')
            );
        } else {
            setSelectedUsers(selectedOptions);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const recipients = selectedUsers
                .map((selectedUser) => {
                    const user = userList.find(
                        (u) => u.userId === selectedUser.value
                    );

                    if (user) {
                        if (selectedType === 'email' && user.email) {
                            return user.email; // Return email if type is email
                        } else if (
                            selectedType === 'sms' &&
                            user.mobileNumber
                        ) {
                            const countryCode = '+91'; // For India, replace with the relevant country code
                            return `${countryCode}${user.mobileNumber}`;
                        } else if (
                            selectedType === 'notification' &&
                            user.mobileNumber
                        ) {
                            return user.mobileNumber; // Return device token for push notifications
                        }
                    }
                    return null;
                })
                .filter((recipient) => recipient !== null);

            const notificationData = {
                title,
                description,
                type: selectedType,
                users: recipients as string[], // Sending recipient email/phone/deviceToken based on type
                schedule: isScheduled ? { publishDate, publishTime } : null, // Include schedule if applicable
            };

            // Send the data to the sharedUtils.ts function
            // await sendNotification(notificationData);
            // console.log('Notification sent successfully!');
            // // navigate(-1);
        } catch (error) {
            console.error('Error sending notification:', error);
        }
    };

    return (
        <div className="mx-auto px-5">
            <h2 className="text-2xl font-semibold">
                {t('notification.title2')}
            </h2>
            <div className="cart relative mx-auto bg-white rounded-2xl p-5 mt-5 w-full">
                <form className="mx-auto w-1/2" onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label
                            className="block text-gray-700 mb-2"
                            htmlFor="title"
                        >
                            {t('notification.title1')}
                        </label>
                        <input
                            className="form-input w-full py-2"
                            id="title"
                            type="text"
                            placeholder="Enter your title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>
                    <div className="mb-4">
                        <label
                            className="block text-gray-700 mb-2"
                            htmlFor="description"
                        >
                            {t('notification.discription')}
                        </label>
                        <textarea
                            className="form-input w-full py-2"
                            id="description"
                            placeholder="Enter your description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        ></textarea>
                    </div>

                    <div className="mb-4">
                        <label
                            className="block text-gray-700 mb-2"
                            htmlFor="type"
                        >
                            {t('notification.type')}
                        </label>
                        <select
                            className="form-input w-full py-2"
                            id="type"
                            value={selectedType}
                            onChange={handleTypeChange}
                        >
                            <option value="email">
                                {t('notification.email')}
                            </option>
                            <option value="sms">{t('notification.sms')}</option>
                            <option value="notification">
                                {t('notification.notification')}
                            </option>
                        </select>
                    </div>

                    <div className="mb-4">
                        <label
                            className="block text-gray-700 mb-2"
                            htmlFor="sendTo"
                        >
                            {t('notification.send_to')}
                        </label>
                        <div className="flex items-center space-x-4">
                            <Select
                                isMulti
                                options={searchResults}
                                value={selectedUsers}
                                onInputChange={handleSearch}
                                onChange={handleSelectUsers}
                                placeholder={
                                    selectedUsers.length === 0
                                        ? 'Search users...'
                                        : 'Select users'
                                }
                                className="w-full"
                                noOptionsMessage={() =>
                                    t('notification.no_results')
                                }
                            />
                        </div>
                    </div>

                    {/* <div className="mb-4 flex items-center justify-between">
                        <label
                            className="block mb-2 font-bold"
                            htmlFor="scheduleToggle"
                        >
                            {t('notification.scheduling_later')}
                        </label>
                        <div onClick={handleToggle} className="cursor-pointer">
                            {isScheduled ? <ToggleOnSVG /> : <ToggleOffSVG />}
                        </div>
                    </div> */}

                    {/* {isScheduled && (
                        <div className="space-y-4">
                            <div className="flex space-x-4">
                                <div className="flex-1">
                                    <label
                                        htmlFor="publishDate"
                                        className="block text-gray-700 mb-2"
                                    >
                                        {t('notification.choose_publish_date')}
                                    </label>
                                    <input
                                        type="date"
                                        id="publishDate"
                                        className="form-input w-full py-2"
                                        value={publishDate}
                                        onChange={(e) =>
                                            setPublishDate(e.target.value)
                                        }
                                    />
                                </div>
                                <div className="flex-1">
                                    <label
                                        htmlFor="publishTime"
                                        className="block text-gray-700 mb-2"
                                    >
                                        {t('notification.choose_publish_time')}
                                    </label>
                                    <input
                                        type="time"
                                        id="publishTime"
                                        className="form-input w-full py-2"
                                        value={publishTime}
                                        onChange={(e) =>
                                            setPublishTime(e.target.value)
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )} */}

                    <div
                        className={`absolute mt-8 flex -bottom-1/3 left-1/2 -translate-x-2/4 pb-3 space-x-4 ${isScheduled ? 'mb-[4rem]' : 'mb-10'}`}
                    >
                        <button
                            className="bg-primary text-white py-2 px-4 rounded"
                            type="submit"
                        >
                            {isScheduled ? 'Publish Later' : 'Publish'}
                        </button>
                        <button
                            className="bg-white border-2 border-blue-700 text-black py-2 px-4 rounded"
                            type="button"
                            onClick={handleCancel}
                        >
                            {t('notification.cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateNotification;
